import React, { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useClickOutside, useScrollToBottom } from '@nx-frontend/shared/hooks';
import { usePreventOutsideScroll } from '@nx-frontend/shared/hooks';
import { setContentOverlayedEvent } from '@nx-frontend/shared/store/content-overlayed';
import { getBgClassNames } from '@nx-frontend/shared/themes';
import { PortalBgType, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { FloatingPopupAlign, useFloatingPopupPosition } from './use-position';

export interface FloatingPopupBaseProps {
  show: boolean;
  children: ReactNode;
  title?: string | React.ReactElement;
  delay?: number;
  onClose?: () => void;
  description?: string | React.ReactElement;
  closeButton?: boolean;
  closeByOutsideClick?: boolean;
  align?: FloatingPopupAlign;
  className?: string[];
  headerClassName?: string[];
  titleClassName?: string[];
  containerClassName?: string[];
  autoScrollToBottom?: boolean;
  wrapper?: 'blank' | 'none';
  position?: 'target' | 'screen_center' | 'screen_full' | 'sidebar_right' | 'sidebar_left';
  targetRef?: React.RefObject<HTMLElement>;
  preventOutsideScroll?: boolean;
  backdrop?: boolean;
  maxSidebarContainerHeight?: string;
  closeOnBackdropClick?: boolean;
  themeColors?: ThemeColorSchemas;
}

export interface FloatingPopupTargetProps extends FloatingPopupBaseProps {
  position?: 'target';
  targetRef: React.RefObject<HTMLElement>;
}

export interface FloatingPopupScreenCenterProps extends FloatingPopupBaseProps {
  position: 'screen_center';
}

export interface FloatingPopupScreenFullProps extends FloatingPopupBaseProps {
  position: 'screen_full';
}

export interface FloatingPopupSidebarRightProps extends FloatingPopupBaseProps {
  position: 'sidebar_right';
}

export interface FloatingPopupSidebarLeftProps extends FloatingPopupBaseProps {
  position: 'sidebar_left';
}

export type FloatingPopupProps =
  | FloatingPopupTargetProps
  | FloatingPopupScreenCenterProps
  | FloatingPopupScreenFullProps
  | FloatingPopupSidebarRightProps
  | FloatingPopupSidebarLeftProps;

export const FloatingPopup: FC<FloatingPopupProps> = ({
  title,
  description,
  show,
  children,
  targetRef,
  align,
  delay = 0,
  className,
  headerClassName,
  titleClassName,
  containerClassName,
  closeButton = true,
  onClose = () => null,
  closeByOutsideClick = true,
  autoScrollToBottom = false,
  wrapper = 'blank',
  position = 'target',
  preventOutsideScroll = true,
  backdrop = false,
  maxSidebarContainerHeight = 'calc(100vh - 78px)',
  closeOnBackdropClick = false,
  themeColors,
}) => {
  const [visible, setVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rootContainerRef = useRef<HTMLDivElement>(null);
  const { top, left } = useFloatingPopupPosition({ show, popupRef, targetRef, align, children });

  const [setContentOverlayed] = useUnit([setContentOverlayedEvent]);

  const isSidebarLeft = position === 'sidebar_left';
  const isSidebarRight = position === 'sidebar_right';
  const isSidebar = isSidebarLeft || isSidebarRight;
  const isScreenCener = position === 'screen_center';
  const isScreenFull = position === 'screen_full';
  const isTarget = position === 'target';

  useEffect(() => {
    setContentOverlayed(show);
  }, [setContentOverlayed, show]);

  usePreventOutsideScroll({ enabled: visible && preventOutsideScroll, elementRef: scrollRef, disableBodyScroll: true });
  useClickOutside({
    enabled: closeByOutsideClick && show,
    elementRef: popupRef,
    onClickOutside: onClose,
    skipSelectors: ['#select-root'],
  });
  useScrollToBottom({ enabled: autoScrollToBottom, scrollRef, trigger: children });

  useLayoutEffect(() => {
    const timeout = setTimeout(() => setVisible(show), show ? delay : 0);
    return () => clearTimeout(timeout);
  }, [delay, show]);

  if (!show) {
    return null;
  }
  const popup = (
    <div
      ref={popupRef}
      className={classNames(
        ' border rounded-lg border-neutral-200 shadow-md transition-opacity',
        {
          'p-2': wrapper === 'blank',
          'opacity-0': !visible,
          'opacity-100': visible,
          'fixed z-50 ': isTarget,
          'invisible': isTarget && top === 0 && left === 0,
          'h-full rounded-tr-none rounded-br-none': isSidebarRight,
        },
        themeColors ? getBgClassNames(themeColors, PortalBgType.SURFACE) : 'bg-white',
        className
      )}
      style={isTarget ? { top, left } : {}}
      data-cy="floating-popup"
    >
      {(title || closeButton) && (
        <div
          className={classNames('flex flex-col items-left', headerClassName, {
            'pt-2 px-2': wrapper === 'blank',
            'pt-4 px-4': wrapper === 'none',
          })}
        >
          <div className={classNames('flex items-center justify-between')}>
            {typeof title === 'string' ? (
              <h3 className={classNames(titleClassName, 'font-inter font-semibold text-base')}>{title}</h3>
            ) : (
              title
            )}
            {closeButton && (
              <div className="ml-auto grow-0 cursor-pointer" onClick={onClose}>
                <Icon
                  icon={IconsMap.CLOSE}
                  className="size-5 text-neutral-500 hover:text-neutral-600 hover:bg-neutral-100 rounded-sm relative z-10"
                />
              </div>
            )}
          </div>
          {typeof description === 'string' ? (
            <div className="flex max-w-96 mb-1.5">
              <p className="font-inter text-sm font-normal break-words">{description}</p>
            </div>
          ) : (
            description
          )}
        </div>
      )}
      <div
        ref={scrollRef}
        style={{ maxHeight: isSidebar ? maxSidebarContainerHeight : undefined }}
        className={classNames('overflow-y-auto overscroll-contain', containerClassName, {
          'max-h-[85vh]': !isSidebar && !isScreenFull,
          'max-h-[95vh]': isSidebar,
          'w-screen h-screen': isScreenFull,
        })}
      >
        {children}
      </div>
    </div>
  );

  if (isTarget) {
    return popup;
  }

  return createPortal(
    <div
      ref={rootContainerRef}
      className={classNames('fixed z-50 inset-0 flex', {
        'bg-neutral-900 bg-opacity-15 backdrop-blur-[2px]-OFF': isScreenCener || isScreenFull || backdrop,
        'items-center justify-center': isScreenCener || isScreenFull,
        'items-start justify-end': isSidebarRight,
        'items-start justify-start pt-14 pl-16': isSidebarLeft,
      })}
      onClick={(event) => {
        if (rootContainerRef.current && event.target !== rootContainerRef.current) {
          return;
        }
        if (closeOnBackdropClick) {
          onClose();
        }
      }}
    >
      {popup}
    </div>,
    document.body
  );
};
